import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AnimatedModal from "components/layout/motion/AnimatedModal";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as CloseIcon } from "assets/svgs/Close.svg";
import { useToast } from "providers/ToastProvider";
import { getInsta360Wifi, putInsta360Wifi } from "lib/r2c2360Api";
import { toUserFacingApiError } from "lib/utils/userFacingApiError";

interface Insta360WifiSettingsDialogProps {
    visible: boolean;
    onClose: () => void;
    baseUrl: string | null;
}

const Insta360WifiSettingsDialog: React.FC<Insta360WifiSettingsDialogProps> = ({
    visible,
    onClose,
    baseUrl,
}) => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const [ssid, setSsid] = useState("");
    const [password, setPassword] = useState("");
    const [host, setHost] = useState("192.168.42.1");
    const [hasPassword, setHasPassword] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadCredentials = useCallback(async () => {
        if (!baseUrl) return;
        try {
            const res = await getInsta360Wifi(baseUrl);
            setSsid(res.ssid || "");
            setHost(res.host || "192.168.42.1");
            setHasPassword(Boolean(res.has_password));
            setPassword("");
        } catch {
            /* service not running */
        }
    }, [baseUrl]);

    useEffect(() => {
        if (!visible) return;
        void loadCredentials();
    }, [loadCredentials, visible]);

    const save = async () => {
        if (!baseUrl) return;
        setSaving(true);
        try {
            await putInsta360Wifi(baseUrl, { ssid, password, host });
            addToast({ type: "success", message: t("insta360.saved") });
            setPassword("");
            setHasPassword(Boolean(ssid) && (hasPassword || Boolean(password)));
            onClose();
        } catch (err) {
            addToast({
                type: "error",
                message: toUserFacingApiError(err, t("insta360.saveFailed")),
            });
        } finally {
            setSaving(false);
        }
    };

    const canSave =
        Boolean(baseUrl) &&
        !saving &&
        Boolean(ssid.trim()) &&
        (hasPassword || Boolean(password.trim()));

    return (
        <AnimatedModal
            visible={visible}
            onClose={onClose}
            className="relative w-full max-w-md rounded-r10 border border-black80 bg-black90 p-6"
        >
            <FunctionalButton
                icon={<CloseIcon />}
                className="absolute right-3 top-3"
                onClick={onClose}
            />
            <h2 className="mb-2 pr-8 text-h4 font-semibold text-white">
                {t("insta360.settingsTitle")}
            </h2>
            <p className="mb-6 text-h5 text-black40">{t("insta360.settingsDescription")}</p>
            {!baseUrl ? (
                <p className="text-h5 text-black60">{t("insta360.connectFirst")}</p>
            ) : (
                <div className="flex flex-col gap-3">
                    <label className="flex flex-col gap-1 text-h5 text-white">
                        {t("insta360.ssid")}
                        <input
                            value={ssid}
                            onChange={(e) => setSsid(e.target.value)}
                            className="rounded-r5 border border-black60 bg-black100 px-3 py-2 outline-none focus:border-primary2"
                            autoComplete="off"
                        />
                    </label>
                    <label className="flex flex-col gap-1 text-h5 text-white">
                        {t("insta360.password")}
                        <input
                            type="password"
                            value={password}
                            placeholder={hasPassword ? t("insta360.passwordKept") : ""}
                            onChange={(e) => setPassword(e.target.value)}
                            className="rounded-r5 border border-black60 bg-black100 px-3 py-2 outline-none focus:border-primary2"
                            autoComplete="new-password"
                        />
                    </label>
                    <label className="flex flex-col gap-1 text-h5 text-white">
                        {t("insta360.host")}
                        <input
                            value={host}
                            onChange={(e) => setHost(e.target.value)}
                            className="rounded-r5 border border-black60 bg-black100 px-3 py-2 outline-none focus:border-primary2"
                        />
                    </label>
                    <p className="text-h5 text-black60">{t("insta360.wifiHint")}</p>
                </div>
            )}
            <div className="mt-6 flex justify-end gap-3">
                <FunctionalButton
                    title={t("common:cancel")}
                    className="rounded-r5 border border-black60 bg-black90 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:border-black40"
                    onClick={onClose}
                    disabled={saving}
                />
                <FunctionalButton
                    title={saving ? t("common:saving") : t("common:save")}
                    className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                    onClick={() => void save()}
                    disabled={!canSave}
                />
            </div>
        </AnimatedModal>
    );
};

export default Insta360WifiSettingsDialog;
